/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The Telephony settings hub in v2 (design reference "Settings · Phone and
 * softphone"): the phone numbers and where each one rings, the calling bar
 * switches, then one row per area with its state and a way in. It draws what
 * TelephonySettingsPage already loaded; the only writes are the calling bar
 * switches, through SoftphoneBarSettingsCard as before.
 *
 * Not in the mock-up's form, for want of a backend: numbers are bought on
 * Twilio and imported on the Numbers page, not bought or ported here; and only the two
 * calling bar switches exist.
 */

import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import { AudioLines, GitBranch, Network, Phone, PlugZap } from "lucide-react";
import SoftphoneBarSettingsCard from "components/Softphone/SoftphoneBarSettingsCard";
import { SettingsRow, SettingsSection, ToneChip } from "components/v2/settings/SettingsBlocks";
import { distinctFriendlyName } from "telephony/callerIdLabel";

const DIRECTION = { both: "Inbound and outbound", inbound: "Inbound", outbound: "Outbound" };

/** Where an inbound call on this number goes, in words. */
export function numberDestination(number, { queues = [], menus = [] } = {}) {
  const queue = queues.find((q) => q.id === number.inbound_queue_id);
  const menu = menus.find((m) => m.id === number.inbound_ivr_id);
  switch (number.inbound_flow) {
    case "direct_user":
      return number.direct_user_id ? "one person" : "nobody yet";
    case "ivr":
      return menu ? `${menu.name} menu` : "a phone menu";
    case "voicemail":
      return "voicemail";
    case "seep_ai":
      return queue ? `SEEP AI, then ${queue.name}` : "SEEP AI";
    default:
      return queue ? queue.name : "no queue yet";
  }
}

function connectionChip(enabled, configured) {
  if (enabled && configured) return <ToneChip tone="success">Connected</ToneChip>;
  if (configured) return <ToneChip tone="warning">Configured · Off</ToneChip>;
  return <ToneChip>Not connected</ToneChip>;
}

function recordingChip(settings) {
  if (!settings?.recording_enabled) return <ToneChip>Recording off</ToneChip>;
  return <ToneChip tone="success">{settings.transcription_enabled ? "Recording · Transcribing" : "Recording on"}</ToneChip>;
}

export default function TelephonyHubV2({ settings, numbers, queues, menus, configured, enabled, onOpen }) {
  const liveMenus = menus.filter((m) => m.is_active).length;
  const shown = numbers.filter((n) => n.is_active !== false);

  return (
    <>
      <SettingsSection
        title="Phone numbers"
        summary="Numbers your team calls from and customers call into."
        flush
        actions={
          configured ? (
            <Button variant="contained" onClick={() => onOpen("/settings/telephony/numbers")}>
              {numbers.length ? "Manage numbers" : "Import numbers"}
            </Button>
          ) : null
        }
      >
        {!configured ? (
          <SettingsRow icon={Phone} title="Connect Twilio first" subtitle="Pearl reads the numbers you own from your Twilio account." onClick={() => onOpen("/settings/telephony/twilio")} />
        ) : shown.length === 0 ? (
          <SettingsRow icon={Phone} title="No numbers yet" subtitle="Import the numbers on your Twilio account, then choose where each one rings." onClick={() => onOpen("/settings/telephony/numbers")} />
        ) : (
          shown.map((n) => (
            <SettingsRow
              key={n.id}
              icon={Phone}
              title={n.e164}
              subtitle={distinctFriendlyName(n)}
              detail={`${DIRECTION[n.direction_capability] || DIRECTION.both}${n.direction_capability === "outbound" ? "" : ` · ${numberDestination(n, { queues, menus })}`}`}
              trailing={n.inbound_flow === "ivr" ? <ToneChip tone="info">Phone menu</ToneChip> : settings?.recording_enabled ? <ToneChip tone="success">Recording on</ToneChip> : null}
            />
          ))
        )}
      </SettingsSection>

      <SoftphoneBarSettingsCard variant="v2" />

      <SettingsSection title="Set-up" summary="Work down the list: connect, import numbers, then decide where calls go." flush>
        <SettingsRow
          icon={PlugZap}
          title="Twilio connection"
          subtitle="Your Twilio Account SID and API key, so Pearl can place and receive calls."
          trailing={connectionChip(enabled, configured)}
          onClick={() => onOpen("/settings/telephony/twilio")}
        />
        <SettingsRow
          icon={Network}
          title="Queues & routing"
          subtitle="Queues built from your teams, and how each one shares out calls."
          trailing={<ToneChip tone={queues.length ? "info" : "neutral"}>{queues.length ? `${queues.length} queue${queues.length === 1 ? "" : "s"}` : "No queues"}</ToneChip>}
          onClick={() => onOpen("/settings/telephony/routing")}
        />
        <SettingsRow
          icon={GitBranch}
          title="Phone menus"
          subtitle={queues.length ? "Press 1 for sales, 2 for service: a choice before callers reach anyone." : "Create a queue first: a menu key has to send the caller somewhere."}
          trailing={<ToneChip tone={liveMenus ? "info" : "neutral"}>{liveMenus ? `${liveMenus} live` : "None live"}</ToneChip>}
          onClick={() => onOpen("/settings/telephony/ivr")}
          disabled={!queues.length}
        />
        <SettingsRow
          icon={AudioLines}
          title="Recording & transcription"
          subtitle={configured ? "What is recorded, what callers are told, how long audio is kept." : "Connect Twilio first: there is nothing to record until calls reach Pearl."}
          trailing={recordingChip(settings)}
          onClick={() => onOpen("/settings/telephony/recording")}
          disabled={!configured}
        />
      </SettingsSection>
    </>
  );
}

TelephonyHubV2.propTypes = {
  settings: PropTypes.object,
  numbers: PropTypes.arrayOf(PropTypes.object).isRequired,
  queues: PropTypes.arrayOf(PropTypes.object).isRequired,
  menus: PropTypes.arrayOf(PropTypes.object).isRequired,
  configured: PropTypes.bool.isRequired,
  enabled: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
};
TelephonyHubV2.defaultProps = { settings: null };
